import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Profile as ProfileType, SaveProfileRequest } from '../../types/profile';
import { profileApi } from '../../services/profileApi';
import { PersonalDetailsSection } from './sections/PersonalDetailsSection';
import { ProfileSummarySection } from './sections/ProfileSummarySection';
import { ProfessionalExperienceSection } from './sections/ProfessionalExperienceSection';
import { SkillsSection } from './sections/SkillsSection';
import { EducationSection } from './sections/EducationSection';
import { VolunteerExperienceSection } from './sections/VolunteerExperienceSection';
import { TrainingSection } from './sections/TrainingSection';
import { PublicationsSection } from './sections/PublicationsSection';
import { CertificationsSection } from './sections/CertificationsSection';
import { ReferencesSection } from './sections/ReferencesSection';
import { testProfileAuth } from '../../debug/testProfile';
import ChatMenu from '../chat/ChatMenu';

const Profile: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState<ProfileType | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // Test auth first, then load profile
    testProfileAuth().then(() => {
      loadProfile();
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadProfile = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('Profile component: Starting loadProfile');
      
      const response = await profileApi.loadProfile();
      console.log('Profile component: API response received', response);
      
      if (response.success && response.data?.content) {
        setProfile(response.data.content);
        console.log('Profile component: Profile data set successfully');
      } else {
        console.log('Profile component: No profile data, creating empty profile');
        setProfile(createEmptyProfile());
      }
    } catch (err: any) {
      console.error('Profile component: Error loading profile:', err);
      
      // Don't show error for auth issues, let axios interceptor handle redirect
      if (err.response?.status === 401 || err.response?.status === 403) {
        console.log('Profile component: Authentication error, will be redirected');
        return; // Don't set error state, let redirect happen
      }
      
      setError('Failed to load profile. Please try again later.');
      setProfile(createEmptyProfile());
    } finally {
      setLoading(false);
    }
  };

  const createEmptyProfile = (): ProfileType => ({
    personal_details: {
      name: '',
      date_of_birth: '',
      marital_status: 'Single',
      nationality: '',
      languages_known: [],
      mobile_numbers: [],
      email: '',
      address: '',
      present_location: ''
    },
    profile_summary: '',
    professional_experience: [],
    volunteer_experience: [],
    education: [],
    skills: [],
    training: [],
    references: [],
    publications: [],
    certifications: []
  });

  const handleSaveProfile = async () => {
    if (!profile) return;

    try {
      setSaving(true);
      setError(null);
      const request: SaveProfileRequest = { data: profile };
      const response = await profileApi.saveProfile(request);
      
      if (response.success) {
        setHasChanges(false);
        // Profile saved successfully
      } else {
        setError('Failed to save profile. Please try again.');
      }
    } catch (err) {
      console.error('Error saving profile:', err);
      setError('Failed to save profile. Please try again later.');
    } finally {
      setSaving(false);
    }
  };

  const updateProfileSection = async <K extends keyof ProfileType>(
    section: K,
    data: ProfileType[K]
  ) => {
    if (!profile) return;
    
    const updatedProfile = {
      ...profile,
      [section]: data
    };
    
    setProfile(updatedProfile);
    setHasChanges(true);
    
    // Auto-save to backend
    try {
      setSaving(true);
      setError(null);
      const request: SaveProfileRequest = { data: updatedProfile };
      const response = await profileApi.saveProfile(request);
      
      if (response.success) {
        setHasChanges(false);
        console.log(`✅ Auto-saved ${section} section successfully`);
      } else {
        console.error(`❌ Failed to auto-save ${section} section`);
        setError('Failed to save changes. Please try the "Save All Changes" button.');
      }
    } catch (err) {
      console.error(`❌ Error auto-saving ${section} section:`, err);
      // Don't show error for auth issues, let axios interceptor handle redirect
      if ((err as any).response?.status !== 401 && (err as any).response?.status !== 403) {
        setError('Failed to save changes. Please try the "Save All Changes" button.');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleCVUploadClick = () => {
    setUploadError(null);
    fileInputRef.current?.click();
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ];

    if (!allowedTypes.includes(file.type)) {
      setUploadError('Invalid file type. Please upload a PDF, DOC, or DOCX file.');
      return;
    }

    // Validate file size (5 MB)
    if (file.size > 5 * 1024 * 1024) {
      setUploadError('File too large. Maximum size is 5 MB.');
      return;
    }

    try {
      setIsUploading(true);
      setUploadError(null);

      const response = await profileApi.uploadCV(file);

      if (response.success) {
        // Reload profile data
        await loadProfile();

        // Show success message
        setError(null);
        alert('CV analyzed successfully! Your profile has been updated.');
      } else {
        setUploadError(response.error || 'Failed to upload CV');
      }
    } catch (err: any) {
      if (err.response?.status !== 401 && err.response?.status !== 403) {
        setUploadError('Failed to upload CV. Please try again.');
      }
    } finally {
      setIsUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-jobesta-gray-20 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-jobesta-blue mx-auto"></div>
          <p className="mt-4 text-jobesta-gray-60">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-jobesta-gray-20 flex items-center justify-center">
        <div className="text-center">
          <p className="text-jobesta-red">Failed to load profile</p>
          <button
            onClick={loadProfile}
            className="mt-4 px-4 py-2 bg-jobesta-blue text-white rounded-jobesta hover:bg-jobesta-darkblue transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-jobesta-gray-20">
      <ChatMenu />
      <div className="flex-1 flex flex-col">
        <div className="flex-1 overflow-y-auto p-4 md:p-8">
          <div className="max-w-7xl mx-auto">
            <div className="mb-8 flex justify-between items-center">
              {hasChanges && (
                <button
                  onClick={handleSaveProfile}
                  disabled={saving}
                  className="px-4 py-2 bg-jobesta-blue text-white rounded-jobesta text-sm font-semibold hover:bg-jobesta-darkblue disabled:opacity-50 transition-colors"
                >
                  {saving ? 'Saving...' : 'Save All Changes'}
                </button>
              )}
            </div>

            {error && (
              <div className="mb-4 p-4 bg-jobesta-red bg-opacity-10 border border-jobesta-red rounded-jobesta">
                <p className="text-jobesta-red">{error}</p>
              </div>
            )}

            {/* CV Upload Section */}
            <div className="mb-6 p-4 bg-white rounded-jobesta shadow-jobesta-card">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-jobesta-gray-90">
                    Upload Your CV
                  </h3>
                  <p className="text-sm text-jobesta-gray-70 mt-1">
                    Let AI analyze your CV and automatically fill your profile
                  </p>
                </div>
                <button
                  onClick={handleCVUploadClick}
                  disabled={isUploading || loading}
                  className="px-4 py-2 bg-jobesta-blue text-white rounded-jobesta hover:bg-jobesta-darkblue transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isUploading ? (
                    <>
                      <span className="inline-block animate-spin">⏳</span>
                      Analyzing...
                    </>
                  ) : (
                    <>
                      <span>📄</span>
                      Analyze my CV with AI
                    </>
                  )}
                </button>
              </div>

              {uploadError && (
                <div className="mt-3 p-3 bg-jobesta-red bg-opacity-10 border border-jobesta-red rounded-jobesta">
                  <p className="text-jobesta-red text-sm">{uploadError}</p>
                </div>
              )}

              {/* Hidden file input */}
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
            </div>

            <div className="space-y-6">
              <PersonalDetailsSection
                data={profile.personal_details}
                onUpdate={(data) => updateProfileSection('personal_details', data)}
              />
              
              <ProfileSummarySection
                summary={profile.profile_summary}
                onUpdate={(summary) => updateProfileSection('profile_summary', summary)}
              />
              
              <ProfessionalExperienceSection
                experiences={profile.professional_experience}
                onUpdate={(experiences) => updateProfileSection('professional_experience', experiences)}
              />
              
              <VolunteerExperienceSection
                experiences={profile.volunteer_experience}
                onUpdate={(experiences) => updateProfileSection('volunteer_experience', experiences)}
              />
              
              <EducationSection
                education={profile.education}
                onUpdate={(education) => updateProfileSection('education', education)}
              />
              
              <SkillsSection
                skills={profile.skills}
                onUpdate={(skills) => updateProfileSection('skills', skills)}
              />
              
              <TrainingSection
                training={profile.training}
                onUpdate={(training) => updateProfileSection('training', training)}
              />
              
              <PublicationsSection
                publications={profile.publications}
                onUpdate={(publications) => updateProfileSection('publications', publications)}
              />
              
              <CertificationsSection
                certifications={profile.certifications}
                onUpdate={(certifications) => updateProfileSection('certifications', certifications)}
              />
              
              <ReferencesSection
                references={profile.references}
                onUpdate={(references) => updateProfileSection('references', references)}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;